import {
  Send,
  Eye,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Clock,
} from "lucide-react";

interface DeliveryStatusBarProps {
  status: string;
  sentAt: string | null;
  viewedAt: string | null;
  acceptedAt: string | null;
  declinedAt: string | null;
  deliveryMethod: string | null;
  recipientPhone: string | null;
  recipientEmail: string | null;
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

interface StepDef {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  time: string | null;
  active: boolean;
  color: string;
}

/**
 * Visual delivery timeline for the admin estimate detail view.
 * Shows: Draft → Sent → Viewed → Accepted/Declined
 */
export function DeliveryStatusBar({
  status,
  sentAt,
  viewedAt,
  acceptedAt,
  declinedAt,
  deliveryMethod,
  recipientPhone,
  recipientEmail,
}: DeliveryStatusBarProps) {
  const steps: StepDef[] = [
    {
      label: "Sent",
      icon: Send,
      time: sentAt,
      active: !!sentAt,
      color: "#2563eb",
    },
    {
      label: "Viewed",
      icon: Eye,
      time: viewedAt,
      active: !!viewedAt,
      color: "#7c3aed",
    },
  ];

  if (status === "declined" || declinedAt) {
    steps.push({
      label: "Declined",
      icon: XCircle,
      time: declinedAt,
      active: !!declinedAt,
      color: "#dc2626",
    });
  } else if (status === "expired") {
    steps.push({
      label: "Expired",
      icon: AlertTriangle,
      time: null,
      active: true,
      color: "#d97706",
    });
  } else {
    steps.push({
      label: "Accepted",
      icon: CheckCircle2,
      time: acceptedAt,
      active: !!acceptedAt,
      color: "#16a34a",
    });
  }

  // If still draft, show nothing
  if (status === "draft") {
    return (
      <div className="flex items-center gap-2 text-xs text-muted-foreground py-2">
        <Clock className="w-3.5 h-3.5" />
        Draft — not yet sent to customer
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Step indicators */}
      <div className="flex items-center gap-1">
        {steps.map((step, i) => {
          const Icon = step.icon;
          return (
            <div key={step.label} className="flex items-center">
              <div
                className="flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium"
                style={{
                  color: step.active ? step.color : "#9ca3af",
                  backgroundColor: step.active
                    ? `${step.color}15`
                    : "#f3f4f6",
                }}
              >
                <Icon className="w-3 h-3" />
                {step.label}
                {step.time && (
                  <span className="font-normal opacity-75">
                    {formatTime(step.time)}
                  </span>
                )}
              </div>
              {i < steps.length - 1 && (
                <div
                  className="w-4 h-px mx-1"
                  style={{
                    backgroundColor: steps[i + 1].active
                      ? steps[i + 1].color
                      : "#d1d5db",
                  }}
                />
              )}
            </div>
          );
        })}
      </div>

      {/* Delivery details */}
      {deliveryMethod && (
        <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
          {deliveryMethod && (
            <span>
              via{" "}
              <span className="font-medium">
                {deliveryMethod === "both"
                  ? "SMS + Email"
                  : deliveryMethod.toUpperCase()}
              </span>
            </span>
          )}
          {recipientPhone && <span>→ {recipientPhone}</span>}
          {recipientEmail && <span>→ {recipientEmail}</span>}
        </div>
      )}
    </div>
  );
}
